// ============================================================================
// permissions.js — single source of truth for module-level access control
// ============================================================================
// Shared by UserManagement.jsx (renders the role permission checkboxes) and
// CustomerVehicleManagement.jsx (enforces module visibility after login).
//
// The backend `roles` table stores `permissions` as a JSON string array of
// permission ids, plus `crm_access` (0/1), `mobile_access` and `level`.
// Login (auth.php) returns the user with { role_id, role_name } only — so the
// app fetches roles.php, matches the user's role by id, and derives access.

// Each module `key` MUST equal the `activeModule` string used in
// CustomerVehicleManagement, and `perm` is the permission id stored in the role.
// `actions` lists the write-actions (create/edit/delete) that can be granted
// separately for that module. The module perm itself always means "view/open".
// A module with no `actions` is view-only (or self-managed, e.g. user_management).
// Action permission ids follow the convention `${key}_${action}`, e.g. customers_create.
const CRUD = ['create', 'edit', 'delete'];
export const APP_MODULES = [
  // Core modules (top navigation bar)
  { key: 'customers',      perm: 'mod_customers',      label: 'Customers',      group: 'core', actions: CRUD },
  { key: 'inventory',      perm: 'mod_inventory',      label: 'Inventory',      group: 'core', actions: CRUD },
  { key: 'invoices',       perm: 'mod_invoices',       label: 'Invoices',       group: 'core', actions: CRUD },
  { key: 'cashflow',       perm: 'mod_cashflow',       label: 'Cashflow',       group: 'core', actions: CRUD },
  { key: 'complaints',     perm: 'mod_complaints',     label: 'Complaints',     group: 'core', actions: CRUD },
  { key: 'reports',        perm: 'mod_reports',        label: 'Reports',        group: 'core' },
  { key: 'redo',           perm: 'mod_services',       label: 'Services',       group: 'core', actions: CRUD },
  // Management modules (Management dropdown menu)
  { key: 'vendors',         perm: 'mod_vendors',         label: 'Vendors',         group: 'management', actions: CRUD },
  { key: 'devices',         perm: 'mod_devices',         label: 'Devices',         group: 'management', actions: CRUD },
  { key: 'vehicle_models',  perm: 'mod_vehicle_models',  label: 'Vehicle Models',  group: 'management', actions: CRUD },
  { key: 'cities',          perm: 'mod_cities',          label: 'Cities',          group: 'management', actions: CRUD },
  { key: 'quotations',      perm: 'mod_quotations',      label: 'Quotations',      group: 'management', actions: CRUD },
  { key: 'employees',       perm: 'mod_employees',       label: 'Employees',       group: 'management', actions: CRUD },
  { key: 'user_management', perm: 'mod_user_management', label: 'User Management', group: 'management' },
  { key: 'nr_vehicles',     perm: 'mod_nr_vehicles',     label: 'NR Vehicles',     group: 'management' },
  { key: 'live_map',        perm: 'mod_live_map',        label: 'Live Map',        group: 'management' },
  { key: 'pipeline',        perm: 'mod_pipeline',        label: 'Sales Pipeline',  group: 'management' },
  { key: 'reconciliation',  perm: 'mod_reconciliation',  label: 'Reconciliation',  group: 'management' },
];

export const ACTION_LABELS = { create: 'Create', edit: 'Edit', delete: 'Delete' };

// The action permission id for a module + action.
export function actionPerm(moduleKey, action) { return `${moduleKey}_${action}`; }

// ── Micro-level report sections ────────────────────────────────────────────
// The Reports module ('mod_reports' = open Reports) is further split into
// individual reports. `head` = the top tab (reportsHead), `type` = the
// sub-report state value used inside CustomerVehicleManagement.
export const REPORT_HEADS = [
  { key: 'inventory', label: 'Inventory' },
  { key: 'invoices',  label: 'Financials' },
  { key: 'sales',     label: 'Sales' },
];
export const REPORT_SECTIONS = [
  { key: 'inv_tech_stock',     head: 'inventory', type: 'tech_stock',        label: 'Technician / Employee Stock' },
  { key: 'inv_stock_issuance', head: 'inventory', type: 'stock_issuance',    label: 'Stock Issuance' },
  { key: 'fin_imei_profit',    head: 'invoices',  type: 'imei_profit',       label: 'IMEI Profit' },
  { key: 'fin_vehicle_profit', head: 'invoices',  type: 'vehicle_profit',    label: 'Vehicle Profit' },
  { key: 'sales_performance',  head: 'sales',     type: 'performance',       label: 'Sales Performance' },
  { key: 'sales_trend',        head: 'sales',     type: 'trend',             label: 'Sales Trend' },
  { key: 'sales_renewal',      head: 'sales',     type: 'renewal',           label: 'Renewal Report' },
  { key: 'sales_upcoming',     head: 'sales',     type: 'upcoming_renewals', label: 'Upcoming Renewals' },
  { key: 'sales_by_area',      head: 'sales',     type: 'by_area',           label: 'Sales by Area' },
];
// The permission id for a report section.
export function reportPerm(sectionKey) { return `report_${sectionKey}`; }
const REPORT_PERMS = new Set(REPORT_SECTIONS.map((s) => reportPerm(s.key)));
export const REPORT_SECTION_BY_KEY = REPORT_SECTIONS.reduce((m, s) => { m[s.key] = s; return m; }, {});

// Groups for the role-form checkbox UI.
export const MODULE_GROUPS = [
  { key: 'core',       name: 'Core Modules',      icon: '📊', desc: 'Main navigation tabs' },
  { key: 'management', name: 'Management Modules', icon: '⚙️', desc: 'Items under the Management menu' },
];

export const MODULE_BY_KEY = {};
const MODULE_PERMS = new Set();
APP_MODULES.forEach((m) => { MODULE_BY_KEY[m.key] = m; MODULE_PERMS.add(m.perm); });

// A role whose level is "super" always sees everything, so the top administrator
// (the "Master" role = level super) can never lock themselves out. Any other role
// (including one literally named "Admin") is fully restrictable by its module list.
export function isSuperRole(role) {
  if (!role) return false;
  return String(role.level || '').toLowerCase() === 'super';
}

// Normalize a role's `permissions` (JSON string or array) into a plain array.
export function parsePermissions(role) {
  if (!role) return [];
  let p = role.permissions;
  if (typeof p === 'string') { try { p = JSON.parse(p); } catch { p = []; } }
  return Array.isArray(p) ? p : [];
}

// Match the logged-in user to their role row (by id first, then by name).
export function resolveUserRole(user, roles) {
  if (!user || !Array.isArray(roles)) return null;
  if (user.role_id != null && user.role_id !== '') {
    const byId = roles.find((r) => String(r.id) === String(user.role_id));
    if (byId) return byId;
  }
  const rn = String(user.role_name || user.role || '').toLowerCase();
  if (rn) {
    const byName = roles.find((r) => String(r.name).toLowerCase() === rn);
    if (byName) return byName;
  }
  return null;
}

// Build the access object used to gate modules. Rollout-safe semantics:
//   • super role                  → sees everything
//   • role not resolved (null)    → sees everything  (fail-open: never lock out
//                                     everyone while the backend/roles settle)
//   • role has 0 module-perms     → "unconfigured"   → sees everything, so
//                                     legacy roles keep working until re-saved
//   • otherwise                   → strict whitelist of the assigned modules
export function buildModuleAccess(role) {
  const superUser = isSuperRole(role);
  const perms = new Set(parsePermissions(role));
  const assignedCount = [...perms].filter((p) => MODULE_PERMS.has(p)).length;
  const configured = !superUser && !!role && assignedCount > 0;

  return {
    role,
    superUser,
    configured, // true → the whitelist is actually enforced
    // Whether this role may use the CRM web app at all (crm_access flag).
    crmAccess: superUser
      ? true
      : (!role || role.crm_access == null ? true : Number(role.crm_access) === 1),
    can(moduleKey) {
      if (!configured) return true; // super / unresolved / unconfigured → all
      const entry = MODULE_BY_KEY[moduleKey];
      if (!entry) return true;      // unknown modules are never blocked
      return perms.has(entry.perm);
    },
    // Can this role perform a write-action (create/edit/delete) in a module?
    // Module perm alone = view only; each action needs its own permission.
    canAction(moduleKey, action) {
      if (!configured) return true;              // super / unresolved / unconfigured → all
      const entry = MODULE_BY_KEY[moduleKey];
      if (!entry) return true;                    // unknown module → don't block
      if (!perms.has(entry.perm)) return false;   // no view access → no actions
      if (!entry.actions || !entry.actions.includes(action)) return true; // no such action concept → don't block
      return perms.has(`${moduleKey}_${action}`);
    },
    // Micro-level: can this role open a specific report section?
    // Reports module perm = open Reports; then each report is a section perm.
    // If Reports is granted but no section is picked → all reports (rollout-safe).
    canReport(sectionKey) {
      if (!configured) return true;                    // super / unresolved / unconfigured → all
      if (!perms.has('mod_reports')) return false;      // no Reports access → nothing
      const anySection = [...perms].some((p) => REPORT_PERMS.has(p));
      if (!anySection) return true;                     // Reports granted, no section chosen → all
      return perms.has(reportPerm(sectionKey));
    },
  };
}
